// @ts-nocheck
/**
 * Course Library Component
 * Teacher course materials management interface with upload, access controls, and CDN distribution
 * Epic 75 - Work Item 27: Teacher Course Materials Sharing
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Toggle,
  InlineNotification,
  SkeletonPlaceholder,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Tile,
  Tag,
  Breadcrumb,
  BreadcrumbItem,
  OverflowMenu,
  OverflowMenuItem,
  Pagination,
  Search,
} from '@carbon/react';
import {
  Add,
  Document,
  Share,
  Security,
  CloudUpload,
  Edit,
  Delete,
  View,
  Download,
  ChartLine,
} from '@carbon/react/icons';
import { FileUploader } from '../storage/FileUploader';
import { useAuth } from '../../hooks/useAuth';

interface CourseMaterial {
  id: string;
  title: string;
  description: string;
  courseId: string;
  courseName: string;
  fileId: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  visibility: 'public' | 'private' | 'course_only';
  accessLevel: 'all_students' | 'specific_classes' | 'invitation_only';
  tags: string[];
  uploadedBy: string;
  uploadedAt: string;
  downloadCount: number;
  viewCount: number;
  status: 'active' | 'archived' | 'pending';
}

const CourseLibrary: React.FC = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState<CourseMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<CourseMaterial | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courseName, setCourseName] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'course_only'>('course_only');
  const [accessLevel, setAccessLevel] = useState<'all_students' | 'specific_classes' | 'invitation_only'>(
    'all_students'
  );
  const [tags, setTags] = useState('');
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);

  const fetchCourseMaterials = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch('/api/v1/courses/materials', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch course materials');
      }

      const data = await response.json();
      setMaterials(data.materials || []);
    } catch (err: any) {
      setError(err.message);
      // Set mock data for development
      setMaterials([
        {
          id: 'mat-001',
          title: 'Introduction to Algebra - Lesson Plan',
          description: 'Comprehensive lesson plan covering basic algebraic concepts for 9th grade students',
          courseId: 'course-math-001',
          courseName: 'Algebra I',
          fileId: 'file-001',
          fileUrl: 'https://example.com/materials/algebra-intro.pdf',
          fileName: 'algebra-introduction-lesson-plan.pdf',
          fileSize: 2457600, // 2.4 MB
          mimeType: 'application/pdf',
          visibility: 'course_only',
          accessLevel: 'all_students',
          tags: ['mathematics', 'algebra', 'lesson-plan', '9th-grade'],
          uploadedBy: user?.email || 'teacher@example.com',
          uploadedAt: '2025-09-20T10:30:00Z',
          downloadCount: 156,
          viewCount: 234,
          status: 'active',
        },
        {
          id: 'mat-002',
          title: 'Chemistry Lab Safety Guidelines',
          description: 'Essential safety protocols and procedures for chemistry laboratory experiments',
          courseId: 'course-chem-001',
          courseName: 'Chemistry 101',
          fileId: 'file-002',
          fileUrl: 'https://example.com/materials/chem-safety.pdf',
          fileName: 'chemistry-lab-safety-guidelines.pdf',
          fileSize: 1843200, // 1.8 MB
          mimeType: 'application/pdf',
          visibility: 'public',
          accessLevel: 'all_students',
          tags: ['chemistry', 'safety', 'laboratory', 'guidelines'],
          uploadedBy: user?.email || 'teacher@example.com',
          uploadedAt: '2025-09-18T14:15:00Z',
          downloadCount: 89,
          viewCount: 167,
          status: 'active',
        },
        {
          id: 'mat-003',
          title: 'World History Timeline Interactive',
          description: 'Interactive timeline covering major world historical events from 1000-2000 CE',
          courseId: 'course-hist-001',
          courseName: 'World History',
          fileId: 'file-003',
          fileUrl: 'https://example.com/materials/history-timeline.html',
          fileName: 'world-history-timeline-interactive.html',
          fileSize: 512000, // 512 KB
          mimeType: 'text/html',
          visibility: 'course_only',
          accessLevel: 'specific_classes',
          tags: ['history', 'timeline', 'interactive', 'world-events'],
          uploadedBy: user?.email || 'teacher@example.com',
          uploadedAt: '2025-09-15T09:45:00Z',
          downloadCount: 45,
          viewCount: 123,
          status: 'active',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseMaterials();
  }, []);

  const handleFileUpload = (fileData: any) => {
    setUploadedFileId(fileData.fileId);
  };

  const handleUploadMaterial = async () => {
    if (!title || !courseId || !uploadedFileId) {
      alert('Please provide title, course, and upload a file');
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch('/api/v1/courses/materials/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          description,
          courseId,
          courseName,
          fileId: uploadedFileId,
          visibility,
          accessLevel,
          tags: tags.split(',').map(t => t.trim()),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to upload course material');
      }

      setUploadModalOpen(false);
      resetForm();
      fetchCourseMaterials();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload material. Please try again.');
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    if (!confirm('Are you sure you want to delete this course material?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/courses/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete course material');
      }

      fetchCourseMaterials();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete material. Please try again.');
    }
  };

  const handleShareMaterial = async (material: CourseMaterial) => {
    const shareUrl = `${window.location.origin}/public/materials/${material.id}`;
    await navigator.clipboard.writeText(shareUrl);
    alert('Share link copied to clipboard!');
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCourseId('');
    setCourseName('');
    setVisibility('course_only');
    setAccessLevel('all_students');
    setTags('');
    setUploadedFileId(null);
  };

  const getVisibilityColor = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'green';
      case 'private':
        return 'red';
      case 'course_only':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredMaterials = materials.filter(
    material =>
      material.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      material.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      material.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const paginatedMaterials = filteredMaterials.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const tableHeaders = [
    { key: 'title', header: 'Title' },
    { key: 'course', header: 'Course' },
    { key: 'type', header: 'Type' },
    { key: 'visibility', header: 'Visibility' },
    { key: 'stats', header: 'Downloads/Views' },
    { key: 'uploaded', header: 'Uploaded' },
    { key: 'actions', header: 'Actions' },
  ];

  const tableRows = paginatedMaterials.map(material => ({
    id: material.id,
    title: (
      <div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{material.title}</div>
        <div
          style={{
            fontSize: 'var(--cds-helper-text-01-font-size)',
            color: 'var(--cds-text-secondary)',
          }}
        >
          {material.description}
        </div>
      </div>
    ),
    course: (
      <div>
        <div style={{ fontWeight: 500 }}>{material.courseName}</div>
        <Tag type="outline" size="sm">
          {material.courseId}
        </Tag>
      </div>
    ),
    type: (
      <div>
        <div style={{ marginBottom: '4px' }}>{material.fileName}</div>
        <div
          style={{
            fontSize: 'var(--cds-helper-text-01-font-size)',
            color: 'var(--cds-text-secondary)',
          }}
        >
          {formatFileSize(material.fileSize)}
        </div>
      </div>
    ),
    visibility: (
      <div>
        <Tag type={getVisibilityColor(material.visibility) as any} size="sm">
          {material.visibility.replace('_', ' ').toUpperCase()}
        </Tag>
        <div
          style={{
            fontSize: 'var(--cds-helper-text-01-font-size)',
            color: 'var(--cds-text-secondary)',
            marginTop: '4px',
          }}
        >
          {material.accessLevel.replace('_', ' ')}
        </div>
      </div>
    ),
    stats: (
      <div>
        <div>↓ {material.downloadCount} downloads</div>
        <div
          style={{
            fontSize: 'var(--cds-helper-text-01-font-size)',
            color: 'var(--cds-text-secondary)',
          }}
        >
          👁 {material.viewCount} views
        </div>
      </div>
    ),
    uploaded: new Date(material.uploadedAt).toLocaleDateString(),
    actions: (
      <OverflowMenu size="sm" flipped>
        <OverflowMenuItem itemText="View" onClick={() => window.open(material.fileUrl, '_blank')} />
        <OverflowMenuItem itemText="Share" onClick={() => handleShareMaterial(material)} />
        <OverflowMenuItem
          itemText="Edit"
          onClick={() => {
            setEditingMaterial(material);
            setTitle(material.title);
            setDescription(material.description);
            setCourseId(material.courseId);
            setCourseName(material.courseName);
            setVisibility(material.visibility);
            setAccessLevel(material.accessLevel);
            setTags(material.tags.join(', '));
            setUploadModalOpen(true);
          }}
        />
        <OverflowMenuItem itemText="Delete" isDelete onClick={() => handleDeleteMaterial(material.id)} />
      </OverflowMenu>
    ),
  }));

  if (loading) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div style={{ marginBottom: 'var(--cds-spacing-05)' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <SkeletonPlaceholder key={i} style={{ height: '80px', marginBottom: '16px' }} />
            ))}
          </div>
        </Column>
      </Grid>
    );
  }

  return (
    <div className="course-library">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbItem href="/admin">Admin Dashboard</BreadcrumbItem>
            <BreadcrumbItem href="/admin/content-management">Content Management</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Course Library</BreadcrumbItem>
          </Breadcrumb>

          {/* Page Header */}
          <div className="page-header mb-6 flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-semibold mb-2">Course Library</h1>
              <p className="text-gray-600">Manage and share educational materials with students across courses</p>
            </div>
            <Button kind="primary" renderIcon={Add} onClick={() => setUploadModalOpen(true)}>
              Upload Material
            </Button>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error loading materials"
              subtitle={error}
              style={{ marginBottom: 'var(--cds-spacing-05)' }}
            />
          )}

          {/* Search and Filters */}
          <div
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              display: 'flex',
              gap: 'var(--cds-spacing-05)',
              alignItems: 'center',
            }}
          >
            <Search
              placeholder="Search materials, courses, or tags..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size="lg"
              style={{ flex: 1 }}
            />
          </div>

          {/* Course Materials Table */}
          <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
            <DataTable headers={tableHeaders} rows={tableRows}>
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>

            {/* Pagination */}
            <Pagination
              page={currentPage}
              totalItems={filteredMaterials.length}
              pageSize={itemsPerPage}
              pageSizes={[5, 10, 20, 50]}
              onChange={({ page }) => setCurrentPage(page)}
              style={{ marginTop: 'var(--cds-spacing-05)' }}
            />
          </Tile>
        </Column>
      </Grid>

      {/* Upload/Edit Modal */}
      <Modal
        open={uploadModalOpen}
        onRequestClose={() => {
          setUploadModalOpen(false);
          setEditingMaterial(null);
          resetForm();
        }}
        modalHeading={editingMaterial ? 'Edit Course Material' : 'Upload Course Material'}
        primaryButtonText={editingMaterial ? 'Update' : 'Upload'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleUploadMaterial}
        size="lg"
      >
        <div className="space-y-4">
          <TextInput
            id="title"
            labelText="Material Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
          />

          <TextArea
            id="description"
            labelText="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
          />

          <div style={{ display: 'flex', gap: 'var(--cds-spacing-05)' }}>
            <TextInput
              id="courseId"
              labelText="Course ID"
              value={courseId}
              onChange={e => setCourseId(e.target.value)}
              required
              style={{ flex: 1 }}
            />

            <TextInput
              id="courseName"
              labelText="Course Name"
              value={courseName}
              onChange={e => setCourseName(e.target.value)}
              required
              style={{ flex: 1 }}
            />
          </div>

          <TextInput
            id="tags"
            labelText="Tags (comma-separated)"
            value={tags}
            onChange={e => setTags(e.target.value)}
            placeholder="e.g., mathematics, algebra, lesson-plan"
          />

          <div style={{ display: 'flex', gap: 'var(--cds-spacing-05)' }}>
            <Select
              id="visibility"
              labelText="Visibility"
              value={visibility}
              onChange={e => setVisibility(e.target.value as any)}
              style={{ flex: 1 }}
            >
              <SelectItem value="course_only" text="Course Only - Course members only" />
              <SelectItem value="public" text="Public - Anyone can access" />
              <SelectItem value="private" text="Private - Only you" />
            </Select>

            <Select
              id="accessLevel"
              labelText="Access Level"
              value={accessLevel}
              onChange={e => setAccessLevel(e.target.value as any)}
              style={{ flex: 1 }}
            >
              <SelectItem value="all_students" text="All Students" />
              <SelectItem value="specific_classes" text="Specific Classes" />
              <SelectItem value="invitation_only" text="Invitation Only" />
            </Select>
          </div>

          {!editingMaterial && (
            <div style={{ marginTop: 'var(--cds-spacing-05)' }}>
              <p
                style={{
                  fontSize: 'var(--cds-label-01-font-size)',
                  fontWeight: 'var(--cds-label-01-font-weight)',
                  marginBottom: 'var(--cds-spacing-03)',
                }}
              >
                Upload File
              </p>
              <FileUploader
                onUploadComplete={handleFileUpload}
                category="course-materials"
                defaultVisibility={visibility}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CourseLibrary;
